import {
  CREDIT_USAGE_ONBOARD_BG_MP4,
  CREDIT_USAGE_ONBOARD_BG_POSTER,
} from '@aibees/shared/constants';
import { memo } from 'react';
import * as Styled from './styles';
import type { CreditUsageBgVideoProps as Props } from './types';

/**
 * Callback ref — runs synchronously when the <video> attaches to the DOM. We
 * set `muted` here because React serialises it as a property and not always as
 * an HTML attribute, so the browser would otherwise see an unmuted autoplay
 * attempt and block it. We also tag the element `fetchpriority="low"`.
 *
 * This clip is a non-critical ambient background, so we don't autoplay it
 * eagerly: paired with `preload="none"`, the download is held until the page
 * has finished loading (window `load`) and the main thread is idle. That lets
 * the pricing page's JS chunks fetch first and keeps the video off the critical
 * path. The lightweight `poster` still paints immediately, so the card shows the
 * clip's first frame during that wait instead of a blank box.
 */
const setBgVideoRef = (el: HTMLVideoElement | null) => {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.setAttribute('fetchpriority', 'low');

  const play = () => {
    el.play().catch(() => {
      /* Autoplay blocked. The `poster` frame stays on screen as the fallback
         (and the element's own dark background behind it — see styles.ts — is
         the backdrop for the overlaid white text). Nothing to do here. */
    });
  };
  const schedule = () =>
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(play, { timeout: 2000 })
      : window.setTimeout(play, 200);

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
};

/**
 * Background looping video for the Credit Usage card. Rendered through a plain
 * styled('video') (not <Box component="video">) so the ref reaches the native
 * element directly and the <source> child can't be stripped by polymorphic
 * prop forwarding. Wrapped in memo() with no props so re-renders from sibling
 * hooks cannot remount the <video> mid-fetch and cancel its Range request.
 */
export const CreditUsageBgVideo = memo(({ className }: Props) => {
  return (
    <Styled.Video
      className={className}
      ref={setBgVideoRef}
      loop
      muted
      playsInline
      preload="none"
      poster={CREDIT_USAGE_ONBOARD_BG_POSTER}
    >
      <source src={CREDIT_USAGE_ONBOARD_BG_MP4} type="video/mp4" />
    </Styled.Video>
  );
});

CreditUsageBgVideo.displayName = 'CreditUsageBgVideo';
